import type { ReactNode } from 'react';
import type { VisualRow } from './grouping';
import { pathKey } from './grouping';
import { DataRow } from './rows/DataRow';
import { GroupHeaderRow } from './rows/GroupHeaderRow';
import { SubtotalRow } from './rows/SubtotalRow';
import { SkeletonRows } from './rows/SkeletonRows';
import type { Density } from './styles';
import type { DataGridColumn, DataGridGrouping, DataGridSummaryItem } from './types';

export interface DataGridBodyProps<T> {
  columns: DataGridColumn<T>[];
  renderableRows: VisualRow<T>[] | null;
  isLoading: boolean;
  skeletonRows: number;
  summary: DataGridSummaryItem<T>[] | undefined;
  density: Density;
  onRowClick?: (row: T, index: number) => void;
  rowKey?: (row: T, index: number) => string | number;
  expanded: Set<string>;
  toggleGroup: (key: string) => void;
  renderGroupHeader?: DataGridGrouping<T>['renderGroupHeader'];
  currency: string;

  // Virtualization ---------------------------------------------------------
  isVirtualized: boolean;
  virtualItems: { index: number; start: number; end: number }[] | null;
  virtualPaddingTop: number;
  virtualPaddingBottom: number;
}

/**
 * Render a single visual row — dispatches on `vr.kind`. Extracted from
 * the body component so it can be reused by both the virtualized and
 * non-virtualized code paths without duplication.
 */
function renderVisualRow<T>(
  vr: VisualRow<T>,
  props: DataGridBodyProps<T>
): ReactNode {
  const {
    columns,
    summary,
    density,
    onRowClick,
    rowKey,
    expanded,
    toggleGroup,
    renderGroupHeader,
    currency,
  } = props;

  if (vr.kind === 'data') {
    const key = rowKey ? rowKey(vr.row, vr.index) : vr.index;
    return (
      <DataRow
        key={`data-${key}`}
        row={vr.row}
        index={vr.index}
        columns={columns}
        density={density}
        onRowClick={onRowClick}
        currency={currency}
      />
    );
  }

  if (vr.kind === 'group-header') {
    const keyStr = pathKey(vr.path);
    return (
      <GroupHeaderRow
        key={`group-${keyStr}`}
        level={vr.level}
        pathLabel={vr.key}
        path={vr.path}
        rows={vr.rows}
        isExpanded={expanded.has(keyStr)}
        columns={columns}
        summary={summary}
        density={density}
        currency={currency}
        onToggle={() => toggleGroup(keyStr)}
        renderGroupHeader={renderGroupHeader}
      />
    );
  }

  if (vr.kind === 'subtotal') {
    if (!summary || summary.length === 0) return null;
    return (
      <SubtotalRow
        key={`subtotal-${pathKey(vr.path)}`}
        level={vr.level}
        path={vr.path}
        rows={vr.rows}
        columns={columns}
        summary={summary}
        density={density}
        currency={currency}
      />
    );
  }

  return null;
}

/**
 * `<tbody>` subtree — handles loading skeletons, the virtualized path
 * (with top/bottom spacer rows), and the plain non-virtualized
 * fallback. All three share the same `renderVisualRow` dispatcher so
 * grouped / ungrouped / virtualized / non-virtualized grids always go
 * through one code path for a given row kind.
 */
export function DataGridBody<T>(props: DataGridBodyProps<T>): ReactNode {
  const {
    columns,
    renderableRows,
    isLoading,
    skeletonRows,
    density,
    isVirtualized,
    virtualItems,
    virtualPaddingTop,
    virtualPaddingBottom,
  } = props;

  return (
    <tbody className="[&_tr:last-child]:border-0">
      {isLoading ? (
        <SkeletonRows count={skeletonRows} columns={columns} density={density} />
      ) : renderableRows && isVirtualized && virtualItems ? (
        <>
          {virtualPaddingTop > 0 && (
            <tr style={{ height: virtualPaddingTop }}>
              <td colSpan={columns.length} className="p-0" />
            </tr>
          )}
          {virtualItems.map((item) =>
            renderVisualRow(renderableRows[item.index], props)
          )}
          {virtualPaddingBottom > 0 && (
            <tr style={{ height: virtualPaddingBottom }}>
              <td colSpan={columns.length} className="p-0" />
            </tr>
          )}
        </>
      ) : renderableRows ? (
        renderableRows.map((vr) => renderVisualRow(vr, props))
      ) : null}
    </tbody>
  );
}
